import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Calendar, LayoutGrid, Maximize2, Minimize2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useCompanyMutation } from '@/hooks/useCompanyMutation'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/lib/AuthProvider'
import { useCompany } from '@/lib/CompanyProvider'
import { ArchivedOrdersTable } from './ArchivedOrdersTable'
import { CheckoutModal } from './CheckoutModal'
import { KanbanCard, type Order } from './KanbanCard'
import { KanbanColumn } from './KanbanColumn'
import { StatusConfirmModal } from './StatusConfirmModal'
import { WhatsAppModal } from './WhatsAppModal'

const COLUMNS = [
  { id: 'AGUARDANDO', title: 'Fila de Espera', color: 'border-l-blue-500' },
  { id: 'LAVANDO', title: 'Lavando', color: 'border-l-yellow-500' },
  { id: 'FINALIZANDO', title: 'Secando/Finalizando', color: 'border-l-orange-500' },
  { id: 'CONCLUIDO', title: 'Pronto para Entrega', color: 'border-l-green-500' },
]

export function KanbanBoard() {
  const queryClient = useQueryClient()
  const { activeCompanyId, companies } = useCompany()
  const { user } = useAuth()
  const currentUserRole = user?.user_metadata?.role
  const activeCompanyName = companies?.find((c) => c.id === activeCompanyId)?.name || 'Empresa'

  const [activeId, setActiveId] = useState<string | null>(null)

  const [checkoutOrder, setCheckoutOrder] = useState<Order | null>(null)
  const isCheckoutOpen = !!checkoutOrder

  const [whatsappOrder, setWhatsappOrder] = useState<Order | null>(null)
  const isWhatsappOpen = !!whatsappOrder

  const [confirmModalState, setConfirmModalState] = useState<{
    isOpen: boolean
    order: Order | null
    newStatus: string | null
  }>({
    isOpen: false,
    order: null,
    newStatus: null,
  })

  // Novos estados para filtros e visualização
  const [dateFilter, setDateFilter] = useState<string>('active')
  const [isCompact, setIsCompact] = useState<boolean>(false)
  const [dateInputVal, setDateInputVal] = useState('')

  useEffect(() => {
    if (['active', 'today', 'yesterday', 'last3days'].includes(dateFilter)) {
      setDateInputVal('')
    } else if (dateFilter) {
      const parts = dateFilter.split('-')
      if (parts.length === 3) {
        setDateInputVal(`${parts[2]}/${parts[1]}/${parts[0]}`)
      }
    }
  }, [dateFilter])

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders', activeCompanyId, dateFilter],
    queryFn: async () => {
      if (!activeCompanyId) return []

      let query = supabase
        .from('orders')
        .select('*')
        .eq('company_id', activeCompanyId)
        .order('created_at', { ascending: true })

      if (dateFilter === 'active') {
        query = query.neq('status', 'PAGO').neq('status', 'ORCAMENTO')
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        yesterday.setHours(0, 0, 0, 0)
        const endYesterday = new Date(yesterday)
        endYesterday.setHours(23, 59, 59, 999)
        query = query
          .gte('created_at', yesterday.toISOString())
          .lte('created_at', endYesterday.toISOString())
      } else if (dateFilter === 'last3days') {
        const start = new Date()
        start.setDate(start.getDate() - 3)
        start.setHours(0, 0, 0, 0)
        const end = new Date()
        end.setHours(23, 59, 59, 999)
        query = query.gte('created_at', start.toISOString()).lte('created_at', end.toISOString())
      }

      const { data, error } = await query
      if (error) throw error
      return data as Order[]
    },
    enabled: !!activeCompanyId && dateFilter === 'active',
  })

  useEffect(() => {
    if (dateFilter !== 'active' && orders.length > 15) {
      setIsCompact(true)
    } else if (dateFilter === 'active' && orders.length <= 15) {
      setIsCompact(false)
    }
  }, [orders.length, dateFilter])

  const { data: collaborators = [] } = useQuery({
    queryKey: ['collaborators', activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return []
      const { data, error } = await supabase
        .from('collaborators')
        .select('id, name')
        .eq('company_id', activeCompanyId)
        .eq('status', 'ATIVO')
      if (error) throw error
      return data
    },
    enabled: !!activeCompanyId,
  })

  useEffect(() => {
    if (!activeCompanyId) return
    const channel = supabase
      .channel('orders_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (_payload) => {
        queryClient.invalidateQueries({ queryKey: ['orders'] })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [queryClient, activeCompanyId])

  const updateOrderStatus = useCompanyMutation({
    mutationFn: async ({
      id,
      status,
      company_id,
    }: {
      id: string
      status: string
      company_id?: string
    }) => {
      const order = orders.find((o) => o.id === id)
      if (!order) return

      const now = new Date().toISOString()
      const parseJson = (val: any) => (typeof val === 'string' ? JSON.parse(val) : val || {})
      const stageTimestamps = { ...parseJson(order.stage_timestamps) }
      const stageDurations = { ...parseJson(order.stage_durations_minutes) }

      stageTimestamps[status] = now

      if (order.status && order.status !== status && stageTimestamps[order.status]) {
        const timeInStageMs = Date.now() - new Date(stageTimestamps[order.status]).getTime()
        const mins = Math.max(0, Math.floor(timeInStageMs / 60000))
        stageDurations[order.status] = (stageDurations[order.status] || 0) + mins
      }

      const updatePayload: any = {
        status,
        company_id,
        stage_timestamps: stageTimestamps,
        stage_durations_minutes: stageDurations,
      }

      if (status === 'CONCLUIDO') {
        updatePayload.completed_at = now
      }

      const { error } = await supabase.from('orders').update(updatePayload).eq('id', id)

      if (error) throw error
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['orders', activeCompanyId] })
      const previousOrders = queryClient.getQueryData<Order[]>(['orders', activeCompanyId])

      queryClient.setQueryData<Order[]>(['orders', activeCompanyId], (old) =>
        old?.map((order) => (order.id === id ? { ...order, status } : order))
      )

      return { previousOrders }
    },
    onError: (err: any, _variables, context) => {
      console.error('Error updating order:', err)
      alert(`Erro ao mudar etapa: ${err.message || JSON.stringify(err)}`)
      if (context?.previousOrders) {
        queryClient.setQueryData(['orders', activeCompanyId], context.previousOrders)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', activeCompanyId] })
    },
  })

  const assignCollaborator = useCompanyMutation({
    mutationFn: async ({ id, assigned_to }: { id: string; assigned_to: string }) => {
      const { error } = await supabase.from('orders').update({ assigned_to }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orders'] }),
  })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const activeOrderId = active.id as string
    const overId = over.id as string

    const activeOrder = orders.find((o) => o.id === activeOrderId)
    if (!activeOrder) return

    let newStatus = overId

    const overOrder = orders.find((o) => o.id === overId)
    if (overOrder) {
      newStatus = overOrder.status
    }

    if (activeOrder.status !== newStatus && COLUMNS.some((c) => c.id === newStatus)) {
      setConfirmModalState({ isOpen: true, order: activeOrder, newStatus })
    }
  }

  const activeOrder = orders.find((o) => o.id === activeId)

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Barra de Filtros / Configurações */}
      <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-800/50 bg-slate-900/40 overflow-x-auto w-full hide-scrollbar">
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setDateFilter('active')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${
              dateFilter === 'active'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <LayoutGrid size={16} />
            Ativos no Pátio
          </button>

          <button
            onClick={() => setDateFilter('today')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${
              dateFilter === 'today'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            title="Ver tabela com os movimentos de hoje"
          >
            <Calendar size={16} />
            Hoje (Tabela)
          </button>

          <button
            onClick={() => setDateFilter('yesterday')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${
              dateFilter === 'yesterday'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <Calendar size={16} />
            Dia Anterior
          </button>

          <button
            onClick={() => setDateFilter('last3days')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${
              dateFilter === 'last3days'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <Calendar size={16} />
            Últimos 3 Dias
          </button>

          <input
            type="text"
            placeholder="dd/mm/aaaa"
            value={dateInputVal}
            onChange={(e) => {
              const raw = e.target.value
              const digits = raw.replace(/\D/g, '').slice(0, 8)
              let formatted = digits
              if (digits.length > 2) {
                formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`
              }
              if (digits.length > 4) {
                formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
              }
              setDateInputVal(formatted)

              if (formatted.length === 10) {
                const parts = formatted.split('/')
                const yyyymmdd = `${parts[2]}-${parts[1]}-${parts[0]}`
                setDateFilter(yyyymmdd)
              }
            }}
            className={`ml-1 bg-slate-800 border-none rounded-lg text-sm px-3 py-1.5 text-slate-300 focus:ring-1 focus:ring-blue-500 outline-none transition-colors shrink-0 w-28 ${
              !['active', 'today', 'yesterday', 'last3days'].includes(dateFilter) &&
              dateFilter !== ''
                ? 'bg-blue-600 text-white placeholder-blue-300'
                : 'placeholder-slate-500'
            }`}
          />
        </div>

        {dateFilter === 'active' && (
          <button
            onClick={() => setIsCompact(!isCompact)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap ml-auto ${
              isCompact
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            title="Reduzir o tamanho dos cards para caber mais veículos na tela"
          >
            {isCompact ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            {isCompact ? 'Modo Normal' : 'Modo Compacto'}
          </button>
        )}
      </div>

      <React.Suspense
        fallback={<div className="p-4 text-center text-slate-400">Carregando painel...</div>}
      >
        {dateFilter === 'active' ? (
          <div className="flex flex-col flex-1 w-full overflow-hidden">
            <div className="flex flex-1 w-full gap-4 sm:gap-6 overflow-x-auto p-4 pb-8 items-start snap-x snap-mandatory scroll-smooth">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {COLUMNS.map((column) => (
                  <KanbanColumn
                    key={column.id}
                    column={column}
                    orders={orders.filter(
                      (o) =>
                        o.status === column.id ||
                        (column.id === 'AGUARDANDO' &&
                          !COLUMNS.some((c) => c.id === o.status) &&
                          o.status !== 'PAGO' &&
                          o.status !== 'ORCAMENTO') ||
                        (column.id === 'CONCLUIDO' &&
                          o.status === 'PAGO' &&
                          dateFilter !== 'active')
                    )}
                    collaborators={collaborators}
                    onCheckoutClick={(order) => setCheckoutOrder(order)}
                    onStatusChange={(order, newStatus) =>
                      setConfirmModalState({ isOpen: true, order, newStatus })
                    }
                    onAssignCollaborator={(orderId, collabId) =>
                      assignCollaborator.mutate({ id: orderId, assigned_to: collabId })
                    }
                    onWhatsAppClick={(order) => setWhatsappOrder(order)}
                    isCompact={isCompact}
                    currentUserRole={currentUserRole}
                    isLoading={isLoading}
                  />
                ))}

                <DragOverlay>
                  {activeOrder ? (
                    <KanbanCard
                      order={activeOrder}
                      collaborators={collaborators}
                      isCompact={isCompact}
                      isOverlay
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        ) : (
          <ArchivedOrdersTable dateFilter={dateFilter} />
        )}

        <CheckoutModal
          isOpen={isCheckoutOpen}
          onClose={() => setCheckoutOrder(null)}
          order={checkoutOrder}
        />

        <StatusConfirmModal
          isOpen={confirmModalState.isOpen}
          onClose={() => setConfirmModalState((prev) => ({ ...prev, isOpen: false }))}
          order={confirmModalState.order}
          newStatus={confirmModalState.newStatus}
          onConfirm={() => {
            if (confirmModalState.order && confirmModalState.newStatus) {
              updateOrderStatus.mutate({
                id: confirmModalState.order.id,
                status: confirmModalState.newStatus,
              })
            }
          }}
        />

        <WhatsAppModal
          isOpen={isWhatsappOpen}
          onClose={() => setWhatsappOrder(null)}
          order={whatsappOrder}
          companyName={activeCompanyName}
        />
      </React.Suspense>
    </div>
  )
}
